import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import formatters from '../shared/formatters';

import {FLAG_ACCENT_COLOR, FLAG_ROW_BACKGROUND, summaryGridColumns} from './clientMonthlyShared';

// Compact, comparison-free stat cards. Values come straight from the backend `totals`,
// which cover every client in the snapshot rather than only the paginated rows. `totals`
// nests the scheduled/actual aggregates; actual cards are omitted for the next month
// (never rendered as $0 / 0 hours).
const buildSummaryCards = (totals, showActual) => {
  const scheduled = totals?.scheduled || null;
  const actual = totals?.actual || null;
  const unpricedHours = formatters.isMissingValue(scheduled?.unpricedClientHours) ? 0 : scheduled.unpricedClientHours;
  const missingRateCount = formatters.isMissingValue(totals?.clientsMissingRate) ? 0 : totals.clientsMissingRate;

  const cards = [
    {
      key: 'clients',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.Clients',
      value: formatters.formatCount(totals?.clientRecordCount),
    },
    {
      key: 'clientsWithHours',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ClientsWithHours',
      value: formatters.formatCount(totals?.clientCount),
    },
    {
      key: 'scheduledHours',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ScheduledHours',
      value: formatters.formatHours(scheduled?.clientHours),
    },
    {
      key: 'scheduledRevenue',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ScheduledRevenue',
      value: formatters.formatCurrency(scheduled?.revenue),
    },
  ];

  if (showActual) {
    cards.push(
        {
          key: 'actualHours',
          labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ActualHours',
          value: formatters.formatHours(actual?.clientHours),
        },
        {
          key: 'actualRevenue',
          labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ActualRevenue',
          value: formatters.formatCurrency(actual?.revenue),
        },
    );
  }

  // Only surface the missing-rate card when clients actually lack a rate; a clean snapshot
  // shows no warning card at all.
  if (missingRateCount > 0) {
    cards.push({
      key: 'missingRates',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.MissingRates',
      value: formatters.formatCount(totals?.clientsMissingRate),
      caption: unpricedHours > 0 ?
        translate('metrics.ClientMetrics.ClientMonthly.Summary.UnpricedHoursCaption', {hours: formatters.formatHours(unpricedHours)}) :
        translate('metrics.ClientMetrics.ClientMonthly.Summary.NoUnpricedHours'),
      warn: true,
    });
  }

  return cards;
};

const ClientMonthlySummaryCards = (props) => {
  const {totals, showActual} = props;

  const cards = React.useMemo(() => buildSummaryCards(totals, showActual), [totals, showActual]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: summaryGridColumns,
        gap: 1.75,
      }}
    >
      {
        cards.map((card) => (
          <Paper
            key={card.key}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: card.warn ? '1px solid #F0D6B0' : '1px solid #E5E5E5',
              backgroundColor: card.warn ? FLAG_ROW_BACKGROUND : '#FFFFFF',
              boxShadow: 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: '13px',
                letterSpacing: '0.04em',
                color: card.warn ? FLAG_ACCENT_COLOR : '#A8A49E',
                mb: 1,
              }}
            >
              <Translate expression={card.labelExpression} />
            </Typography>
            <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '26px', color: '#1A1916'}}>
              {card.value}
            </Typography>
            {
              card.caption &&
              <Typography sx={{fontSize: '10px', lineHeight: '13px', color: '#A8A49E', mt: 0.5}}>
                {card.caption}
              </Typography>
            }
          </Paper>
        ))
      }
    </Box>
  );
};

ClientMonthlySummaryCards.propTypes = {
  totals: PropTypes.object,
  showActual: PropTypes.bool.isRequired,
};

export default React.memo(ClientMonthlySummaryCards);
